import { Injectable } from '@angular/core';
import { DataOptions } from '@xpo-ltl/data-api';
import {
  GetUserPreferencePath,
  GetUserPreferenceResp,
  InspectionsApiService,
  UpdateUserPreferenceRqst,
  UserPreference
} from '@xpo-ltl/sdk-inspections';
import { EMPTY, Observable } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { GeneralPreferences } from '../classes/general-preferences';
import { SicShiftPreference } from '../classes/sic-shift-preference';
import { ErrorMessageActions } from '../enums/error-message-actions.enum';
import { ListName } from '../enums/list-name.enum';
import { UserPreferenceAppKeys } from '../enums/userPreference-appKeys.enum';
import { RetryStrategy } from '../operators/retry-strategy';
import { RequestValidator } from '../validators/request.validator';
import { ErrorHandlingService } from './error-handling.service';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root'
})
export class UserPreferencesService {
  private static readonly DEFAULT_UPDATE_TIMEOUT = 60000;
  private static readonly DEFAULT_RETRY_DELAY = 1000;
  private static readonly DEFAULT_UPDATE_RETRY_COUNT = 0;
  private static readonly DEFAULT_DATA_OPTIONS = <DataOptions>{
    loadingOverlayEnabled: false,
    toastOnError: false
  };
  private static readonly DEFAULT_GET_TIMEOUT = 15000;
  private static readonly DEFAULT_RETRY_COUNT = 4;
  private static readonly DEFAULT_LOADING_MESSAGE = `Loading. Please Wait...`;
  private readonly SOURCE_NAME: string = `User Preferences`;

  // This will store the preferences loaded from the backend
  private generalPreferences: GeneralPreferences;
  private sicShiftPreference: SicShiftPreference = new SicShiftPreference();

  constructor(
    private inspectionsApiService: InspectionsApiService,
    private errorHandling: ErrorHandlingService,
    private retryStrategy: RetryStrategy,
    private userService: UserService
  ) {}

  public getGeneralPreferences(): GeneralPreferences {
    return this.generalPreferences;
  }

  protected setGeneralPreferences(preferences: GeneralPreferences): void {
    this.generalPreferences = preferences;
  }

  public saveGeneralUserPreferences(preferences: GeneralPreferences): void {
    // We update the field in the service
    this.setGeneralPreferences(preferences);

    // Then we save the user preferences in database
    const jsonString = preferences.toJson();
    this.updateUserPreference(UserPreferenceAppKeys.GeneralSettings, jsonString);
  }

  public loadGeneralUserPreferences(): Observable<string> {
    return this.loadUserPreference(UserPreferenceAppKeys.GeneralSettings).pipe(
      tap((preference: string) => {
        // Store the preferences in the service, for them to be available in other parts of the app
        if (preference) {
          // We parse the value of general user preferences
          const preferences = new GeneralPreferences();
          preferences.setPreferencesFromJson(preference);

          // And we set the generalPreferences
          this.generalPreferences = preferences;
        }
      }),
      catchError((error) => {
        this.errorHandling.handleResponseError(error, ErrorMessageActions.LOADING, this.SOURCE_NAME);

        return EMPTY;
      })
    );
  }

  public saveListUserPreferences(listName: ListName, propertiesJsonString: string): void {
    this.updateUserPreference(UserPreferenceAppKeys[listName.replace(/\s/g, '')], propertiesJsonString);
  }

  public loadListUserPreferences(listName: ListName): Observable<string> {
    return this.loadUserPreference(UserPreferenceAppKeys[listName.replace(/\s/g, '')]);
  }

  public getSicShiftUserPreference(): SicShiftPreference {
    return this.sicShiftPreference;
  }

  public updateSicShiftUserPreference(aSicShiftPreference: SicShiftPreference): void {
    this.sicShiftPreference = aSicShiftPreference;
    return this.updateUserPreference(UserPreferenceAppKeys.SicShift, this.sicShiftPreference.convertToJson());
  }

  public loadSicShiftUserPreference(): Observable<SicShiftPreference> {
    return this.loadUserPreference(UserPreferenceAppKeys.SicShift).pipe(
      map((preference: string) => {
        if (preference) {
          this.sicShiftPreference.setPreferencesFromJson(preference);
        }
        return this.sicShiftPreference;
      })
    );
  }

  private updateUserPreference(key: string, preference: string): void {
    RequestValidator.validateStringNotNullOrEmpty(key, 'App Key');
    RequestValidator.validateStringNotNullOrEmpty(this.userService.currentUserEmployeeId, 'Employee ID');

    const request = new UpdateUserPreferenceRqst();
    request.appKey = key;
    request.employeeId = this.userService.currentUserEmployeeId;
    request.userPreference = new UserPreference();
    request.userPreference.appName = 'INSPECTION';
    request.userPreference.preference = preference;

    this.inspectionsApiService
      .updateUserPreference(request, UserPreferencesService.DEFAULT_DATA_OPTIONS)
      .pipe(
        this.retryStrategy.retryStrategy(
          UserPreferencesService.DEFAULT_UPDATE_TIMEOUT,
          UserPreferencesService.DEFAULT_RETRY_DELAY,
          UserPreferencesService.DEFAULT_UPDATE_RETRY_COUNT,
          null,
          'UPDATE-USER-PREFERENCE'
        ),
        take(1),
        catchError((error) => {
          this.errorHandling.handleResponseError(error, ErrorMessageActions.UPDATING, this.SOURCE_NAME);

          return EMPTY;
        })
      )
      .subscribe(() => {
        // ONLY FOR TEST ENVIRONMENT: Refresh the page after deleting Sic and Shift User Preference
        if (request.appKey === UserPreferenceAppKeys.SicShift && request.userPreference.preference === '{}') {
          location.reload();
        }
      });
  }

  private loadUserPreference(key: string): Observable<string> {
    RequestValidator.validateStringNotNullOrEmpty(key, 'App Key');

    return this.userService.isUserLoggedIn$.pipe(
      switchMap(() => {
        RequestValidator.validateStringNotNullOrEmpty(this.userService.currentUserEmployeeId, 'Employee ID');

        const path = new GetUserPreferencePath();
        path.appKey = key;
        path.employeeId = this.userService.currentUserEmployeeId;

        return this.inspectionsApiService.getUserPreference(path, UserPreferencesService.DEFAULT_DATA_OPTIONS).pipe(
          this.retryStrategy.retryStrategy(
            UserPreferencesService.DEFAULT_GET_TIMEOUT,
            UserPreferencesService.DEFAULT_RETRY_DELAY,
            UserPreferencesService.DEFAULT_RETRY_COUNT,
            UserPreferencesService.DEFAULT_LOADING_MESSAGE,
            'GET-USER-PREFERENCE'
          ),
          take(1),
          map((response: GetUserPreferenceResp) => {
            if (response?.userPreference?.preference?.trim().length > 0) {
              return response.userPreference.preference.trim();
            } else {
              return null;
            }
          }),
          catchError((error) => {
            this.errorHandling.handleResponseError(error, ErrorMessageActions.LOADING, this.SOURCE_NAME);

            return EMPTY;
          })
        );
      })
    );
  }
}
